import React, { useState } from 'react';
import { Code, Copy, Check, ExternalLink, ShieldCheck, Sparkles, Terminal, Globe, Layers } from 'lucide-react';

export const EmbedSDKPanel: React.FC = () => {
  const [selectedFormat, setSelectedFormat] = useState<'script' | 'react' | 'nextjs' | 'vue'>('script');
  const [copied, setCopied] = useState(false);
  const [showLiveWidgetPreview, setShowLiveWidgetPreview] = useState(false);

  const getCodeSnippet = () => {
    switch (selectedFormat) {
      case 'script':
        return `<!-- 1. Add AutoFix.run Self-Healing Sentinel to your <head> -->
<script 
  src="https://cdn.autofix.run/v1/webmcp-sentinel.min.js" 
  data-project-id="proj_live_89f2a4"
  data-auto-heal="true"
  data-mode="balanced"
  defer>
</script>

<!-- That's it! Any unhandled runtime exception or NaN arithmetic 
     will now invoke navigator.modelContext to heal in-memory. -->`;

      case 'react':
        return `// 1. Install via npm
// npm install @autofix/webmcp-sentinel

import React from 'react';
import { initWebMCPSentinel } from '@autofix/webmcp-sentinel';

// Initialize in your application root (e.g. index.tsx / App.tsx)
initWebMCPSentinel({
  apiKey: process.env.AUTOFIX_API_KEY,
  autoHeal: true,
  quarantineThresholdMs: 250,
  onPatchApplied: (patch) => {
    console.log('[AutoFix] Live in-memory hotpatch active:', patch.id);
  }
});`;

      case 'nextjs':
        return `// app/layout.tsx (Next.js App Router)
import Script from 'next/script';

export default function RootLayout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <Script
          src="https://cdn.autofix.run/v1/webmcp-sentinel.min.js"
          data-project-id={process.env.NEXT_PUBLIC_AUTOFIX_ID}
          strategy="beforeInteractive"
        />
      </head>
      <body>{children}</body>
    </html>
  );
}`;

      case 'vue':
        return `// main.js (Vue 3)
import { createApp } from 'vue';
import App from './App.vue';
import { autoFixPlugin } from '@autofix/webmcp-vue';

const app = createApp(App);

app.use(autoFixPlugin, {
  projectId: 'vue_ecommerce_app',
  autoHeal: true,
  captureAsyncState: true,
});

app.mount('#app');`;
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(getCodeSnippet());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Header Banner */}
      <div
        style={{
          background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.08) 0%, rgba(99, 102, 241, 0.08) 100%)',
          border: '1px solid rgba(56, 189, 248, 0.3)',
          borderRadius: 8,
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: 'linear-gradient(135deg, #0284c7 0%, #2563eb 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
            }}
          >
            <Globe size={20} />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>
              Universal WebMCP Drop-in SDK Generator
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-2)' }}>
              Protect any external React, Next.js, Vue, or static Netlify website in under 60 seconds
            </div>
          </div>
        </div>

        <button
          className="btn btn-sm btn-primary"
          onClick={() => setShowLiveWidgetPreview(!showLiveWidgetPreview)}
          style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}
        >
          <Sparkles size={12} /> {showLiveWidgetPreview ? 'Hide Floating Sentinel' : 'Preview Live Floating Widget'}
        </button>
      </div>

      {/* Format Selector Bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="tab-bar">
          <button
            className={`tab ${selectedFormat === 'script' ? 'active' : ''}`}
            onClick={() => setSelectedFormat('script')}
            style={{ fontSize: 11, padding: '3px 10px' }}
          >
            HTML / Netlify Script Tag
          </button>
          <button
            className={`tab ${selectedFormat === 'react' ? 'active' : ''}`}
            onClick={() => setSelectedFormat('react')}
            style={{ fontSize: 11, padding: '3px 10px' }}
          >
            React Hook (npm)
          </button>
          <button
            className={`tab ${selectedFormat === 'nextjs' ? 'active' : ''}`}
            onClick={() => setSelectedFormat('nextjs')}
            style={{ fontSize: 11, padding: '3px 10px' }}
          >
            Next.js App Router
          </button>
          <button
            className={`tab ${selectedFormat === 'vue' ? 'active' : ''}`}
            onClick={() => setSelectedFormat('vue')}
            style={{ fontSize: 11, padding: '3px 10px' }}
          >
            Vue 3 Plugin
          </button>
        </div>

        <button
          className="btn btn-sm"
          onClick={copyToClipboard}
          style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}
        >
          {copied ? <Check size={12} style={{ color: 'var(--green)' }} /> : <Copy size={12} />}
          {copied ? 'Copied to Clipboard!' : 'Copy Code'}
        </button>
      </div>

      {/* Code Snippet Box */}
      <div
        style={{
          background: '#090d16',
          border: '1px solid var(--border)',
          borderRadius: 6,
          padding: 12,
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          lineHeight: 1.5,
          position: 'relative',
          overflowX: 'auto',
        }}
      >
        <pre style={{ margin: 0, color: 'var(--text-1)' }}>{getCodeSnippet()}</pre>
      </div>

      {/* Features Overview */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        <div
          className="card"
          style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 4 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: 'var(--text-1)' }}>
            <ShieldCheck size={14} style={{ color: 'var(--green)' }} />
            Zero Bundle Overhead
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-2)' }}>
            Only 4.2 KB gzip. Defers execution until an uncaught error boundary fires.
          </div>
        </div>

        <div
          className="card"
          style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 4 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: 'var(--text-1)' }}>
            <Layers size={14} style={{ color: 'var(--accent)' }} />
            W3C WebMCP Native
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-2)' }}>
            Detects and registers with <code>window.navigator.modelContext</code> in modern browsers.
          </div>
        </div>

        <div
          className="card"
          style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 4 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: 'var(--text-1)' }}>
            <Terminal size={14} style={{ color: '#38bdf8' }} />
            Automatic Pull Requests
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-2)' }}>
            Opens a clean Git Pull Request with regression tests for every healed incident.
          </div>
        </div>
      </div>

      {/* Live Floating Widget Simulation */}
      {showLiveWidgetPreview && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 9999,
            background: '#090d16',
            border: '1px solid var(--accent)',
            borderRadius: 12,
            padding: 14,
            boxShadow: '0 10px 30px rgba(0,0,0,0.6), 0 0 20px rgba(16,185,129,0.3)',
            maxWidth: 320,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            animation: 'slideUp 0.3s ease',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: 'var(--green)',
                  boxShadow: '0 0 8px var(--green)',
                }}
              />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>
                AutoFix Sentinel v1.0
              </span>
            </div>
            <button
              onClick={() => setShowLiveWidgetPreview(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-3)',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              ✕
            </button>
          </div>

          <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.4 }}>
            Connected to <code>navigator.modelContext</code>. Monitoring 24 active UI components for runtime faults.
          </div>

          <div
            style={{
              background: 'rgba(16,185,129,0.1)',
              padding: '6px 8px',
              borderRadius: 6,
              fontSize: 10,
              color: 'var(--green)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            STATUS: 0 crashes · System nominal · 182ms MTTR
          </div>
        </div>
      )}
    </div>
  );
};
